import { Component, OnDestroy, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { LanguagePickerComponent } from "../../components/language-picker/language-picker.component";
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { LoginRequest, ProblemDetails } from "../../services";
import { HttpErrorResponse } from "@angular/common/http";
import { AuthenticationService } from "../../infrastructure/authentication.service";
import { FormInputComponent } from "../../components/form-input/form-input.component";
import { MessageService } from "../../infrastructure/message.service";
import { AuthenticationSecretsService } from "../../infrastructure/authentications-secrets.service";

// @ts-expect-error asf
const $ = window["$"];

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrl: "./login.component.scss",
  standalone: true,
  imports: [
    TranslateModule,
    RouterLink,
    LanguagePickerComponent,
    ReactiveFormsModule,
    FormInputComponent,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm!: FormGroup;
  submitting = signal(false);

  requires2fa = signal<boolean>(false);

  passwordVisible = false;

  returnUrl = "/portal/home";

  constructor(
    private readonly fb: NonNullableFormBuilder,
    private readonly authService: AuthenticationService,
    private readonly authSecretService: AuthenticationSecretsService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translateService: TranslateService,
    private readonly messageService: MessageService
  ) {
    document.documentElement.setAttribute("data-bs-theme", "dark");

    this.authService.currentUser.subscribe((authenticationResult) => {
      if (authenticationResult.user) {
        this.router.navigateByUrl(this.returnUrl);
      }
    });
  }

  ngOnInit() {
    this.returnUrl =
      this.route.snapshot.queryParams["returnUrl"] || "/portal/home";

    this.loginForm = this.fb.group({
      email: new FormControl("", [
        Validators.required,
        Validators.email,
        Validators.maxLength(255),
      ]),
      password: new FormControl("", [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(255),
      ]),
      tfa: new FormControl("", [
        Validators.minLength(6),
        Validators.maxLength(11),
      ]),
      passwordRemember: new FormControl(this.authSecretService.persistToken),
    });

    this.loginForm.valueChanges.subscribe(() => {
      const forms = document.getElementsByClassName("needs-validation");
      forms[0].classList.add("was-validated");
    });

    $(document).ready(function () {
      $('[data-bs-toggle="tooltip"]').tooltip();
    });
  }

  ngOnDestroy(): void {
    $(".tooltip").not(this).hide();
  }

  get email(): string {
    return this.loginForm.get("email")?.value;
  }

  get password(): string {
    return this.loginForm.get("password")?.value;
  }

  get rememberPassword(): boolean {
    return this.loginForm.get("passwordRemember")?.value;
  }

  onSubmit() {
    if (this.loginForm.valid) {
      this.submitting.set(true);

      const request: LoginRequest = {
        email: this.email,
        password: this.password,
      };

      if (this.loginForm.get("tfa")?.value) {
        if (this.loginForm.get("tfa")!.value.length === 11) {
          request.twoFactorRecoveryCode = this.loginForm.get("tfa")?.value;
        } else {
          request.twoFactorCode = this.loginForm.get("tfa")?.value;
        }
      }

      this.authService.login(
        request,
        this.rememberPassword,
        this.returnUrl,
        (err: HttpErrorResponse) => {
          if (
            err.status === 401 &&
            (err.error as ProblemDetails).detail
              ?.toLowerCase()
              .includes("2fa required")
          ) {
            this.requires2fa.set(true);
            this.loginForm.get("tfa")?.addValidators(Validators.required);

            const forms = document.getElementsByClassName("needs-validation");
            forms[0].classList.add("was-validated");
          } else if (
            err.status === 401 &&
            (err.error as ProblemDetails).detail
              ?.toLowerCase()
              .includes("user must create 2fa")
          ) {
            this.router.navigateByUrl("generate-tfa");
          } else if (
            err.status === 401 &&
            err.error.detail.includes("User account is deactivated")
          ) {
            this.messageService.publishError(
              this.translateService.instant("InvalidLoginDeleted")
            );
          } else if (err.status === 401) {
            this.messageService.publishError(
              this.translateService.instant("InvalidLoginCredentials")
            );
          }

          this.submitting.set(false);
        }
      );
    }
  }
}
